/**
 * Performance monitoring utilities for Talentino Frontend
 * Provides tools for measuring component render times, API performance, and overall app performance
 */

// Performance metrics storage
const performanceMetrics = {
  componentRenders: new Map(),
  apiCalls: new Map(),
  navigationTiming: null,
  webVitals: new Map(),
};

// Development flag - only log in development
const isDevelopment = import.meta.env.DEV;

/**
 * Measure component render performance
 * @param {string} componentName - Name of the component being measured
 * @param {number} renderTime - Time taken to render in milliseconds
 * @param {Object} metadata - Additional metadata about the render
 */
export function logComponentRender(componentName, renderTime, metadata = {}) {
  if (!isDevelopment) return;

  const metrics = performanceMetrics.componentRenders.get(componentName) || {
    count: 0,
    totalTime: 0,
    maxTime: 0,
    minTime: Infinity,
    averageTime: 0,
    lastRenderTime: 0,
    timestamps: [],
  };

  metrics.count++;
  metrics.totalTime += renderTime;
  metrics.maxTime = Math.max(metrics.maxTime, renderTime);
  metrics.minTime = Math.min(metrics.minTime, renderTime);
  metrics.averageTime = metrics.totalTime / metrics.count;
  metrics.lastRenderTime = renderTime;
  metrics.timestamps.push({
    time: Date.now(),
    renderTime,
    metadata,
  });

  // Keep only last 100 timestamps
  if (metrics.timestamps.length > 100) {
    metrics.timestamps.shift();
  }

  performanceMetrics.componentRenders.set(componentName, metrics);

  // Log to console in development
  if (renderTime > 16) { // Threshold for "slow" renders (60fps = 16ms per frame)
    console.warn(`[Performance] Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms`, metadata);
  }
}

/**
 * Measure API call performance
 * @param {string} endpoint - API endpoint being called
 * @param {number} duration - Duration of the API call in milliseconds
 * @param {boolean} success - Whether the call was successful
 * @param {Object} metadata - Additional metadata about the API call
 */
export function logApiCall(endpoint, duration, success = true, metadata = {}) {
  if (!isDevelopment) return;

  const metrics = performanceMetrics.apiCalls.get(endpoint) || {
    count: 0,
    totalTime: 0,
    successCount: 0,
    failureCount: 0,
    maxTime: 0,
    minTime: Infinity,
    averageTime: 0,
    timestamps: [],
  };

  metrics.count++;
  metrics.totalTime += duration;
  metrics.maxTime = Math.max(metrics.maxTime, duration);
  metrics.minTime = Math.min(metrics.minTime, duration);
  metrics.averageTime = metrics.totalTime / metrics.count;

  if (success) {
    metrics.successCount++;
  } else {
    metrics.failureCount++;
  }

  metrics.timestamps.push({
    time: Date.now(),
    duration,
    success,
    metadata,
  });

  // Keep only last 50 timestamps per endpoint
  if (metrics.timestamps.length > 50) {
    metrics.timestamps.shift();
  }

  performanceMetrics.apiCalls.set(endpoint, metrics);

  // Log slow API calls
  if (duration > 1000) { // Threshold for slow API calls (1 second)
    console.warn(`[Performance] Slow API call to ${endpoint}: ${duration.toFixed(2)}ms`);
  }
}

/**
 * Get all performance metrics
 * @returns {Object} All collected performance metrics
 */
export function getPerformanceMetrics() {
  return {
    componentRenders: Object.fromEntries(performanceMetrics.componentRenders),
    apiCalls: Object.fromEntries(performanceMetrics.apiCalls),
    navigationTiming: performanceMetrics.navigationTiming,
    webVitals: Object.fromEntries(performanceMetrics.webVitals),
    timestamp: Date.now(),
  };
}

/**
 * Clear all performance metrics
 */
export function clearPerformanceMetrics() {
  performanceMetrics.componentRenders.clear();
  performanceMetrics.apiCalls.clear();
  performanceMetrics.navigationTiming = null;
  performanceMetrics.webVitals.clear();
}

/**
 * Start a performance measurement
 * @param {string} name - Name of the measurement
 * @returns {Function} A function to call to end the measurement and log it
 */
export function startMeasurement(name) {
  const startTime = performance.now();
  
  return (metadata = {}) => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (name.startsWith('API:')) {
      const endpoint = name.replace('API:', '');
      logApiCall(endpoint, duration, true, metadata);
    } else if (name.startsWith('COMPONENT:')) {
      const componentName = name.replace('COMPONENT:', '');
      logComponentRender(componentName, duration, metadata);
    } else {
      // Generic measurement
      console.debug(`[Performance] ${name}: ${duration.toFixed(2)}ms`, metadata);
    }
    
    return duration;
  };
}

/**
 * Initialize navigation timing
 */
export function initNavigationTiming() {
  if (typeof window !== 'undefined' && window.performance) {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation) {
      performanceMetrics.navigationTiming = {
        dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcpConnect: navigation.connectEnd - navigation.connectStart,
        request: navigation.responseStart - navigation.requestStart,
        response: navigation.responseEnd - navigation.responseStart,
        domInteractive: navigation.domInteractive,
        domContentLoaded: navigation.domContentLoadedEventEnd,
        loadEvent: navigation.loadEventEnd,
        total: navigation.duration,
      };
    }
  }
}

/**
 * Log a web vital metric
 * @param {string} name - Name of the web vital (CLS, FID, LCP, etc.)
 * @param {number} value - The metric value
 * @param {Object} metadata - Additional metadata
 */
export function logWebVital(name, value, metadata = {}) {
  performanceMetrics.webVitals.set(name, {
    value,
    timestamp: Date.now(),
    metadata,
  });
  
  if (isDevelopment) {
    console.log(`[Web Vitals] ${name}: ${value}`, metadata);
  }
}

// Initialize navigation timing on load
if (typeof window !== 'undefined') {
  window.addEventListener('load', initNavigationTiming);
}

export default {
  logComponentRender,
  logApiCall,
  getPerformanceMetrics,
  clearPerformanceMetrics,
  startMeasurement,
  initNavigationTiming,
  logWebVital,
};